import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import * as api from "../../api/index";

const AllUsers = () => {
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [chatError, setChatError] = useState(""); // chat error
  const [search, setSearch] = useState(""); // Name filter

  const navigate = useNavigate();

  const token = localStorage.getItem("token");
  let userId = null;

  try {
    if (token) {
      const decoded = JSON.parse(atob(token.split(".")[1])); // decode JWT manually
      userId = decoded.id || decoded._id;
    }
  } catch {
    userId = null;
  }

  const fetchUsers = async (pageNumber) => {
    try {
      setLoading(true);
      setError("");
      const res = await api.getAllUsers(pageNumber, 20); // call backend
      if (res.data.success) {
        setUsers(res.data.users || []);
        setTotalPages(res.data.totalPages || 1);
        setTotalUsers(res.data.totalUsers || 0);
      } else {
        setError("Failed to load users.");
      }
    } catch (err) {
   
      setError("⚠️ Could not load users. Try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(page);
  }, [page]);

  const startChat = async (receiver) => {
    setChatError("");
    if (!userId) return setChatError("You must be logged in to chat.");
    try {
      const res = await api.createConversation({
        senderId: userId,
        receiverId: receiver._id,
      });
      if (res.data.success) {
        navigate(`/chat/${res.data.conversation._id}`, {
          state: { otherUser: res.data?.seller || receiver },
        });
      }
    } catch (err) {
   
      setChatError("Failed to start chat. Please try again.");
    }
  };

  // Filter users by Name (case-insensitive)
const filteredUsers = users.filter((user) =>
  (user.Name || "").toLowerCase().includes(search.toLowerCase())
);

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="mb-4 px-4 py-2 rounded-lg hover:bg-gray-300 transition"
      >
        ← Back
      </button>

      <h1 className="text-3xl font-bold text-gray-800 mb-2 text-center">
        All Users
      </h1>

      {/* Total Users */}
      <p className="text-center text-gray-600 mb-4">
        Total Users: {totalUsers}
      </p>

      {/* Search Filter */}
      <div className="max-w-2xl mx-auto mb-6">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by Name..."
          className="w-full p-2 border rounded-lg shadow-sm focus:outline-none focus:ring focus:border-blue-300"
        />
      </div>

      {loading ? (
        <p className="text-center text-gray-600">Loading users...</p>
      ) : error ? (
        <p className="text-center text-red-500">{error}</p>
      ) : (
        <>
          {/* Users Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
        {filteredUsers.map((user) => (
  <div
    key={user._id}
    className="bg-white rounded-xl shadow-md p-5 flex flex-col gap-3 hover:shadow-lg transition justify-between"
  >
    <div>
      <div className="flex items-center gap-4">
        <img
          src={
            user.profileImage 
              ? `${user.profileImage}`
              : "https://res.cloudinary.com/dvuq6vmiy/image/upload/v1758633376/ChatGPT_Image_Sep_23_2025_06_14_30_AM_sef3w0.png"
          }
          alt={user.Name}
          className="w-16 h-16 rounded-full object-cover border"
        />
        <div>
          <h2 className="text-lg font-semibold text-gray-800">
            {user.Name}
          </h2>
          <p className="text-gray-600 text-sm">{user.Email}</p>
          
          {/* 📍 Location Display */}
          {(user.state || user.lga) && (
            <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
              📍 {user.lga ? `${user.lga}, ` : ""}{user.state || ""}
            </p>
          )}

          <span
            className={`inline-block mt-2 px-3 py-1 text-xs font-medium rounded-full ${
              user.typeofuser === "Customer"
                ? "bg-blue-100 text-blue-700"
                : user.typeofuser === "Seller"
                ? "bg-green-100 text-green-700"
                : "bg-purple-100 text-purple-700"
            }`}
          >
            {user.typeofuser || "Unknown"}
          </span>
        </div>
      </div>

      {/* 🏷️ Seller Categories / Fields Space */}
      {user.typeofuser === "Seller" && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <p className="text-xs font-semibold text-gray-700 mb-1">Categories / Fields:</p>
          <div className="flex flex-wrap gap-1">
            {user.topfield && user.topfield.length > 0 ? (
              user.topfield.map((field, idx) => (
                <span key={idx} className="bg-gray-100 text-gray-600 px-2 py-0.5 text-xs rounded-md">
                  {field}
                </span>
              ))
            ) : user.subCategory && user.subCategory.length > 0 ? (
              user.subCategory.map((sub, idx) => (
                <span key={idx} className="bg-gray-100 text-gray-600 px-2 py-0.5 text-xs rounded-md">
                  {sub}
                </span>
              ))
            ) : (
              <span className="text-xs text-gray-400 italic">No categories listed</span>
            )}
          </div>
        </div>
      )}

      {/* Date created */}
      <p className="text-xs text-gray-500 mt-3">
        Joined: {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : "N/A"}
      </p>
    </div>

    {/* Actions */}
    <div>
      {user.typeofuser === "Customer" && (
        <>
          <button
            onClick={() => startChat(user)}
            className="w-full mt-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
          >
            Chat
          </button>
          {chatError && (
            <p className="text-red-500 text-sm mt-1">{chatError}</p>
          )}
        </>
      )}
    </div>
  </div>
))}
          </div>

          {/* Pagination Controls */}
          <div className="flex justify-center items-center gap-4 mt-8">
            <button
              onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
              disabled={page === 1}
              className="px-4 py-2 bg-gray-300 rounded-lg hover:bg-gray-400 disabled:opacity-50"
            >
              Prev
            </button>
            <span className="text-gray-700 font-medium">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() =>
                setPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={page === totalPages}
              className="px-4 py-2 bg-gray-300 rounded-lg hover:bg-gray-400 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default AllUsers;
